import { useCallback, useMemo, useState, useEffect } from 'react';
import { DomainsModal } from './modals/DomainsModal';
import { SkillsForDomainModal } from './modals/SkillsForDomainModal';
import { DomainKPIsSection } from './sections/domains_kpi/DomainKPIsSection';
import { DomainSection } from './sections/domains/DomainSection';
import { DomainSkillsSection } from './sections/skills/DomainSkillsSection';
import { SkillsKPIsSection } from './sections/skills_kpi/SkillsKPIsSection';
import { useDomainesCached } from './hooks_back/useDomainesCached';
import { useSkillsCached } from './hooks_back/useSkillsCached';

export const OverviewView = ({ data, setActiveView, toggleSkillCompletion }) => {
  const {
    domaines,
    loadingDomaines,
    createDomaine,
    deleteDomaine,
    refetchDomaines,
    isFromCache: domainesFromCache,
    lastFetch: domainesLastFetch
  } = useDomainesCached({
    cacheDuration: 10 * 60 * 1000,
    autoRefresh: true
  });

  const {
    skills,
    loadingSkills,
    createSkill,
    updateSkill,
    updateSkillWithNameChange, // NOUVEAU
    deleteSkill,
    reorderSkills: reorderSkillsAPI,
    rateSkill,
    getSkillsByDomain,
    refetchSkills,
    isFromCache: skillsFromCache,
    lastFetch: skillsLastFetch
  } = useSkillsCached(null, {
    cacheDuration: 5 * 60 * 1000,
    autoRefresh: true
  });

  const [isSkillsForDomainModalOpen, setIsSkillsForDomainModalOpen] = useState(false);
  const [isDomainsModalOpen, setIsDomainsModalOpen] = useState(false);
  const [selectedDomainForSkills, setSelectedDomainForSkills] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [skillsData, setSkillsData] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const handleGlobalRefresh = useCallback(async (forceRefetch = false) => {
    try {
      if (forceRefetch) {
        await Promise.all([
          refetchDomaines(),
          refetchSkills()
        ]);
      }
      
      setRefreshKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Erreur lors du refresh global:', error);
    }
  }, [refetchDomaines, refetchSkills]);

  const allDomains = useMemo(() => {
    return domaines.map(domaine => ({
      id: domaine.id_domaine,
      name: domaine.nom_domaine,
      icon: domaine.icon || '👤',
      description: domaine.description,
      score: domaine.score_evaluation || 0,
      level: domaine.niveau_evaluation || 'Débutant',
      type_domaine: domaine.type_domaine,
      isCustom: domaine.type_domaine === 'custom'
    }));
  }, [domaines, refreshKey]);

  useEffect(() => {
    if (selectedDomain === null && allDomains.length > 0) {
      setSelectedDomain(allDomains[0].id);
    } else if (selectedDomain !== null && !allDomains.some(d => d.id === selectedDomain)) {
      setSelectedDomain(allDomains[0]?.id || null);
    }
  }, [allDomains, selectedDomain, refreshKey]);

  const buildSkillsDataFromDB = useMemo(() => {
    const skillsDataFromDB = {};
    skills.forEach(skill => {
      skillsDataFromDB[skill.nom_skill] = {
        description: skill.description || '',
        niveau_maitrise_requis: skill.niveau_maitrise_requis || 'Débutant',
        training_link: skill.training_link || '',
        certification: skill.certification || '',
        rating: skill.rating || 0,
        ordre_affichage: skill.ordre_affichage || 0,
        id_skill: skill.id_skill
      };
    });
    return { ...skillsDataFromDB, ...skillsData };
  }, [skills, skillsData, refreshKey]);

  const allSkillsByDomain = useMemo(() => {
    const skillsByDomainFromDB = getSkillsByDomain();
    const formatted = {};
    Object.keys(skillsByDomainFromDB).forEach(domainId => {
      const sortedSkills = skillsByDomainFromDB[domainId]
        .sort((a, b) => (a.ordre_affichage || 0) - (b.ordre_affichage || 0));
      formatted[domainId] = sortedSkills.map(skill => skill.nom_skill);
    });
    return formatted;
  }, [skills, getSkillsByDomain, refreshKey]);

  const handleDomainsUpdated = useCallback(async (shouldForceRefetch = true) => {
    try {
      await handleGlobalRefresh(true);
      
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 100);
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour des domaines:', error);
    }
  }, [handleGlobalRefresh]);

  const handleSkillsUpdated = useCallback(async (shouldForceRefetch = false) => {
    try {
      if (shouldForceRefetch) {
        await handleGlobalRefresh(true);
      } else {
        await refetchSkills();
        
        if (skillsLastFetch && (Date.now() - skillsLastFetch) < 30000) {
          await handleGlobalRefresh(false);
        } else {
          await handleGlobalRefresh(true);
        }
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour des compétences:', error);
    }
  }, [refetchSkills, skillsLastFetch, handleGlobalRefresh]);

  const handleDeleteDomain = useCallback(async (domainId) => {
    const domainToDelete = allDomains.find(d => d.id === domainId);
    if (!domainToDelete) {
      console.warn('Domaine à supprimer non trouvé:', domainId);
      return;
    }

    if (domainToDelete.type_domaine === 'systeme') {
      console.warn('Impossible de supprimer un domaine système');
      return;
    }

    try {
      if (selectedDomain === domainId) {
        const remainingDomains = allDomains.filter(d => d.id !== domainId);
        setSelectedDomain(remainingDomains[0]?.id || null);
      }
      
      const result = await deleteDomaine(domainId);
      
      if (result.success) {
        await handleGlobalRefresh(false);
      } else {
        await handleGlobalRefresh(true);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du domaine:', error);
      await handleGlobalRefresh(true);
    }
  }, [allDomains, selectedDomain, deleteDomaine, handleGlobalRefresh]);

  const addSkillToDomain = async (skillData) => {
    console.log('🎯 Ajout de compétence:', skillData, 'au domaine:', selectedDomainForSkills);
    
    if (!selectedDomainForSkills) {
      console.error('❌ Aucun domaine sélectionné pour l\'ajout');
      throw new Error('Aucun domaine sélectionné');
    }

    try {
      const isSkillObject = typeof skillData === 'object' && skillData.nom_skill;
      let apiData;

      if (isSkillObject) {
        apiData = {
          nom_skill: skillData.nom_skill,
          description: skillData.description || '',
          niveau_maitrise_requis: skillData.niveau_maitrise_requis || 'Débutant',
          rating: skillData.rating || 0,
          ordre_affichage: skillData.ordre_affichage || 0,
          id_domaine: selectedDomainForSkills
        };

        if (skillData.training_link && skillData.training_link.trim() !== '') {
          apiData.training_link = skillData.training_link;
        }

        if (skillData.certification && skillData.certification.trim() !== '') {
          apiData.certification = skillData.certification;
        }
      } else {
        apiData = {
          nom_skill: skillData,
          description: '',
          niveau_maitrise_requis: 'Débutant',
          rating: 0,
          ordre_affichage: 0,
          id_domaine: selectedDomainForSkills
        };
      }
      
      console.log('📤 Données envoyées à l\'API:', apiData);
      const result = await createSkill(apiData);

      if (!result.success) {
        console.error('❌ Échec création compétence:', result.error);
        throw new Error(result.error);
      } else {
        console.log('✅ Compétence créée avec succès:', result.skill);
        const skillName = apiData.nom_skill;
        setSkillsData(prev => ({
          ...prev,
          [skillName]: {
            description: apiData.description,
            niveau_maitrise_requis: apiData.niveau_maitrise_requis,
            training_link: apiData.training_link,
            certification: apiData.certification,
            rating: apiData.rating,
            ordre_affichage: apiData.ordre_affichage
          }
        }));

        await handleSkillsUpdated(false);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de compétence:', error);
      throw error;
    }
  };

  // 🔧 FONCTION CORRIGÉE POUR LA SUPPRESSION
  const removeSkillFromDomain = async (skillName) => {
    console.log('🗑️ Tentative de suppression de:', skillName);
    console.log('📍 Domaine sélectionné pour skills:', selectedDomainForSkills);
    console.log('📍 Toutes les compétences disponibles:', skills.map(s => ({ nom: s.nom_skill, id: s.id_skill, domaine: s.id_domaine })));

    // Utiliser le domaine du modal OU le domaine sélectionné comme fallback
    const targetDomainId = selectedDomainForSkills || selectedDomain;
    
    if (!targetDomainId) {
      console.error('❌ Aucun domaine sélectionné pour la suppression');
      throw new Error('Aucun domaine sélectionné pour la suppression');
    }

    // Chercher la compétence dans le domaine cible
    let skill = skills.find(s =>
      s.nom_skill === skillName && s.id_domaine === targetDomainId
    );

    // Si pas trouvée dans le domaine spécifique, chercher par nom seulement (fallback)
    if (!skill) {
      console.warn(`⚠️ Compétence "${skillName}" non trouvée dans le domaine ${targetDomainId}, recherche globale...`);
      skill = skills.find(s => s.nom_skill === skillName);
    }

    if (!skill) {
      console.error(`❌ Compétence "${skillName}" non trouvée`);
      throw new Error(`Compétence "${skillName}" non trouvée`);
    }

    console.log('🎯 Compétence trouvée:', { 
      id: skill.id_skill, 
      nom: skill.nom_skill, 
      domaine: skill.id_domaine 
    });

    try {
      const result = await deleteSkill(skill.id_skill);
      console.log('📤 Résultat suppression API:', result);

      if (!result.success) {
        console.error('❌ Échec suppression API:', result.error);
        throw new Error(result.error || 'Erreur lors de la suppression');
      } else {
        console.log('✅ Compétence supprimée avec succès');
        
        // Nettoyer les données locales
        setSkillsData(prev => {
          const newData = { ...prev };
          delete newData[skillName];
          return newData;
        });

        // Rafraîchir les données
        await handleSkillsUpdated(false);
        
        return result;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      throw error;
    }
  };

  const handleReorderSkills = async (domainId, reorderedSkills) => {
    console.log('🔄 Réorganisation des compétences:', { domainId, reorderedSkills });
    
    try {
      const result = await reorderSkillsAPI(domainId, reorderedSkills);
      if (!result.success) {
        console.warn('⚠️ Échec de la réorganisation:', result.error);
      } else {
        console.log('✅ Réorganisation réussie');
        await handleSkillsUpdated(false);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la réorganisation:', error);
    }
  };

  const handleUpdateSkillData = async (skillName, updatedData) => {
    console.log('✏️ Mise à jour de compétence:', { skillName, updatedData });
    
    const skill = skills.find(s => s.nom_skill === skillName);

    if (!skill) {
      console.error(`❌ Compétence "${skillName}" non trouvée pour mise à jour`);
      return;
    }

    try {
      const apiData = {
        description: updatedData.description,
        niveau_maitrise_requis: updatedData.niveau_maitrise_requis,
        training_link: updatedData.training_link,
        certification: updatedData.certification,
        rating: updatedData.rating
      };

      // Nettoyer les valeurs undefined
      Object.keys(apiData).forEach(key => {
        if (apiData[key] === undefined) delete apiData[key];
      });

      console.log('📤 Données de mise à jour:', apiData);
      const result = await updateSkill(skill.id_skill, apiData);

      if (result.success) {
        console.log('✅ Mise à jour réussie');
        setSkillsData(prev => ({
          ...prev,
          [skillName]: {
            ...prev[skillName],
            ...updatedData
          }
        }));

        await handleSkillsUpdated(false);
      } else {
        console.error('❌ Échec mise à jour:', result.error);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
    }
  };

  // NOUVEAU : Fonction pour gérer le changement de nom de compétence
  const handleUpdateSkillWithNameChange = async (oldSkillName, newSkillData) => {
    console.log('🔄 Changement de nom de compétence:', { oldSkillName, newSkillData });
    
    try {
      const result = await updateSkillWithNameChange(oldSkillName, newSkillData);
      
      if (result.success) {
        console.log('✅ Changement de nom réussi:', result);
        
        // Mettre à jour les données locales
        setSkillsData(prev => {
          const newData = { ...prev };
          
          // Supprimer l'ancienne entrée
          delete newData[oldSkillName];
          
          // Ajouter la nouvelle entrée
          newData[newSkillData.name] = {
            description: newSkillData.description,
            niveau_maitrise_requis: newSkillData.niveau_maitrise_requis,
            training_link: newSkillData.training_link,
            certification: newSkillData.certification,
            rating: prev[oldSkillName]?.rating || 0, // Conserver la note existante
            ordre_affichage: prev[oldSkillName]?.ordre_affichage || 0,
            id_skill: result.skill?.id_skill
          };
          
          return newData;
        });

        // Rafraîchir les données
        await handleSkillsUpdated(false);
        
        return result;
      } else {
        console.error('❌ Échec changement de nom:', result.error);
        return result;
      }
    } catch (error) {
      console.error('❌ Erreur lors du changement de nom:', error);
      return { success: false, error: error.message };
    }
  };

  const handleRateSkill = async (skillName, rating) => {
    console.log('⭐ Notation de compétence:', { skillName, rating });
    
    const skill = skills.find(s => s.nom_skill === skillName);

    if (!skill) {
      console.error(`❌ Compétence "${skillName}" non trouvée pour notation`);
      return;
    }

    try {
      const result = await rateSkill(skill.id_skill, rating);

      if (result.success) {
        console.log('✅ Notation réussie');
        setSkillsData(prev => ({
          ...prev,
          [skillName]: {
            ...prev[skillName],
            rating: rating
          }
        }));

        await handleSkillsUpdated(false);
      } else {
        console.error('❌ Échec notation:', result.error);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la notation:', error);
    }
  };

  const getSelectedDomainInfo = useCallback(() => {
    if (!selectedDomainForSkills) return null;

    const domainInfo = allDomains.find(d => d.id === selectedDomainForSkills);
    return domainInfo ? {
      id: domainInfo.id,
      name: domainInfo.name,
      icon: domainInfo.icon,
      description: domainInfo.description,
      isCustom: domainInfo.isCustom
    } : null;
  }, [selectedDomainForSkills, allDomains]);

  const getCurrentDomainSkills = useCallback(() => {
    if (!selectedDomainForSkills) return [];
    const skills = allSkillsByDomain[selectedDomainForSkills] || [];
    console.log(`📋 Compétences du domaine ${selectedDomainForSkills}:`, skills);
    return skills;
  }, [selectedDomainForSkills, allSkillsByDomain]);

  const handleOpenSkillsModal = useCallback(() => {
    console.log('📂 Ouverture modal compétences pour domaine:', selectedDomain);
    
    if (selectedDomain) {
      setSelectedDomainForSkills(selectedDomain);
      setIsSkillsForDomainModalOpen(true);
    } else if (allDomains.length > 0) {
      const firstDomain = allDomains[0].id;
      console.log('📂 Aucun domaine sélectionné, utilisation du premier:', firstDomain);
      setSelectedDomain(firstDomain);
      setSelectedDomainForSkills(firstDomain);
      setIsSkillsForDomainModalOpen(true);
    } else {
      console.warn('❌ Aucun domaine disponible pour ouvrir le modal');
    }
  }, [selectedDomain, allDomains]);

  const handleOpenDomainsModal = useCallback(() => {
    setIsDomainsModalOpen(true);
  }, []);

  const handleCloseDomainsModal = useCallback(async () => {
    setIsDomainsModalOpen(false);
    await handleGlobalRefresh(true);
  }, [handleGlobalRefresh]);

  const handleCloseSkillsModal = useCallback(async () => {
    console.log('🔒 Fermeture modal compétences');
    setIsSkillsForDomainModalOpen(false);
    setSelectedDomainForSkills(null); // Reset du domaine sélectionné pour skills
    await handleGlobalRefresh(false);
  }, [handleGlobalRefresh]);

  // Calculs de statistiques
  const totalSkills = skills.length;
  const skillsWithRating = skills.filter(skill => skill.rating && skill.rating > 0);
  const completedCount = skillsWithRating.length;
  const progressPercentage = totalSkills > 0 ? Math.round((completedCount / totalSkills) * 100) : 0;

  const skillsByLevel = {
    debutant: skills.filter(s => s.niveau_maitrise_requis === 'Débutant').length,
    intermediaire: skills.filter(s => s.niveau_maitrise_requis === 'Intermédiaire').length,
    avance: skills.filter(s => s.niveau_maitrise_requis === 'Avancé').length,
    expert: skills.filter(s => s.niveau_maitrise_requis === 'Expert').length
  };

  const skillsWithCertification = skills.filter(s => s.certification && s.certification.trim() !== '').length;
  const skillsWithTraining = skills.filter(s => s.training_link && s.training_link.trim() !== '').length;

  // Données mock
  const mockUserAssessment = {};
  const mockCompletedSkills = skillsWithRating.map(skill => skill.nom_skill);
  const mockGetSkillsToImprove = () => skills.filter(skill => !skill.rating || skill.rating < 3);

  if (loadingDomaines || loadingSkills) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18574F] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-[525px]">
          <DomainSection
            categories={allDomains}
            userAssessment={mockUserAssessment}
            onOpenSkillsModal={handleOpenDomainsModal}
            customSkillDomains={[]}
            onDomainSelect={setSelectedDomain}
            selectedDomain={selectedDomain}
            onDeleteDomain={handleDeleteDomain}
            domainesData={domaines}
            skillsData={skills}
            refreshTrigger={refreshKey}
            onRefreshPage={handleGlobalRefresh}
          />
        </div>

        <div className="h-[525px]">
          <DomainSkillsSection
            selectedDomain={selectedDomain}
            categories={allDomains}
            customSkillDomains={[]}
            skillsByDomain={allSkillsByDomain}
            skillsData={buildSkillsDataFromDB}
            onOpenSkillsModal={handleOpenSkillsModal}
            onRemoveSkill={removeSkillFromDomain}
            onReorderSkills={handleReorderSkills}
            onUpdateSkillData={handleUpdateSkillData}
            onUpdateSkillWithNameChange={handleUpdateSkillWithNameChange} // NOUVEAU
            onRateSkill={handleRateSkill}
            domainesData={domaines}
            skillsDataRaw={skills}
            refreshTrigger={refreshKey}
            onRefreshPage={handleGlobalRefresh}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-[510px]">
          <DomainKPIsSection 
            domainesData={domaines}
            skillsData={skills}
            refreshTrigger={refreshKey}
            onRefreshPage={handleGlobalRefresh}
          />
        </div>

        <div className="h-[510px]">
          <SkillsKPIsSection
            categories={allDomains}
            completedSkills={mockCompletedSkills}
            getSkillsToImprove={mockGetSkillsToImprove}
            skillsByDomain={allSkillsByDomain}
            skillsData={buildSkillsDataFromDB}
            customSkillDomains={[]}
            setActiveView={setActiveView}
            totalSkills={totalSkills}
            skillsWithRating={skillsWithRating.length}
            progressPercentage={progressPercentage}
            skillsByLevel={skillsByLevel}
            skillsWithCertification={skillsWithCertification}
            skillsWithTraining={skillsWithTraining}
            domainesData={domaines}
            skillsDataRaw={skills}
            refreshTrigger={refreshKey}
            onRefreshPage={handleGlobalRefresh}
          />
        </div>
      </div>

      <SkillsForDomainModal
        isOpen={isSkillsForDomainModalOpen}
        onClose={handleCloseSkillsModal}
        domainInfo={getSelectedDomainInfo()}
        skills={getCurrentDomainSkills()}
        onAddSkill={addSkillToDomain}
        onRemoveSkill={removeSkillFromDomain}
      />

      <DomainsModal
        isOpen={isDomainsModalOpen}
        onClose={handleCloseDomainsModal}
        onDomainsUpdated={handleDomainsUpdated}
        onCreateDomain={createDomaine}
      />
    </div>
  );
};